import {filter, firstValueFrom, map, of, Subject, timeout} from 'rxjs';
import {v4 as uuid} from 'uuid';

import {
  Analytics,
  type ClientResponse,
  Conversation,
  CONVERSATION_STORAGE_PREFIX,
  initObservability,
  type Observability,
  REQUEST_ACTION_DEFAULT_TIMEOUT_MS,
  type RequestAction,
  type TriggerParams,
} from '@stripe-internal/conversations-client';
import {createStore} from '@stripe-internal/state-manager';
import {
  MessageFromParent,
  MessageFromParentType,
  MessageToParentType,
  Visibility,
} from '@stripe-internal/support-conversations-controller';

import type {Api} from '../../core/lib/api/api';
import {
  AppBusMessage,
  AppCloseHandler,
  AppConfig,
  AppProfile,
  DEFAULT_VIEW_MODE,
} from '../../core/types';
import {
  AssistantState,
  AssistantStateStore,
  LegacyConversationsController,
  LegacyThreadsApp as ThreadsApp,
} from './types';
import {
  canFetchConversation,
  ConversationViewModel,
  NEW_CONVERSATION_ID,
} from '../viewModels/LegacyConversationViewModel';
import {ClientFetch} from '../../core/lib/util/clientFetch';
import {createApi} from '../../core/lib/api/createApi';
import {createParentMessageBus} from '../../core/lib/parentMessageBus/createParentMessageBus';
import {devLog} from '../../core/lib/util/dev';
import {
  getTriggerPointOpenBehavior,
  isCheckoutStudioTriggerPoint,
  isStartFreshTriggerPoint,
  type TriggerPointOpenBehavior,
} from '../../core/lib/util/triggerPoints';
import {HistoryViewModel} from '../viewModels/LegacyHistoryViewModel';
import {isLiveSupportChannel as isSupportChannel} from '../conversations/storage';
import {METRIC} from '../../core/lib/util/clientMetrics';
import {ParentMessageBus} from '../../core/lib/parentMessageBus/ParentMessageBus';
import {
  requestVisibilityChange,
  sendWidgetInitialized,
} from '../../core/lib/parentMessageBus/messages';
import {V1ConversationsController} from '../conversations/V1ConversationsController';
import {wrapFetch} from '../../core/lib/wrapFetch';

function normalizeVisibility(
  visibility: AppConfig['initialVisibility'],
): Visibility {
  if (visibility === 'closed') {
    return Visibility.Closed;
  }

  if (visibility === 'minimized') {
    return Visibility.Minimized;
  }

  return Visibility.Open;
}

// Pre-cutover runtime, retained for history-off Threads during the rollout.
export class ThreadsAppImpl implements ThreadsApp {
  readonly runtime = 'legacy' as const;

  private _api: Api;

  private _conversations: LegacyConversationsController;

  private _conversationViewModel: ConversationViewModel;

  private _historyViewModel: HistoryViewModel;

  private _store: AssistantStateStore;

  private _parentMessageBus: ParentMessageBus;

  private _parentMessageSubject = new Subject<AppBusMessage>();

  private _observability: Observability;

  private _isActive = false;

  private _didInitialLoad = false;

  private _conversationLoadStartTime?: number;

  constructor(
    config: AppConfig,
    profile: AppProfile,
    clientFetch: ClientFetch,
    parentMessageBus?: ParentMessageBus,
    onClose?: AppCloseHandler,
    observability: Partial<Observability> = {},
    withHistory = true,
  ) {
    this._store = createStore<AssistantState>({
      initialized: false,
      withHistory,
      config,
      profile,
      triggerParams: config.triggerPoint
        ? {triggerPoint: config.triggerPoint}
        : undefined,
      pendingConfirmation: null,
      visibility: normalizeVisibility(config.initialVisibility),
      viewMode: config.viewMode ?? DEFAULT_VIEW_MODE,
      onCloseHandler: onClose,
    });

    this._parentMessageBus =
      parentMessageBus ??
      createParentMessageBus({
        audience: config.audience,
        entryPoint: config.entryPoint,
        parentOrigin: config.parentOrigin ?? window.origin,
      });

    this._observability = initObservability({
      ...observability,
      analytics: {
        ...observability.analytics,
        requestAnalyticsEventId: this.parentMessageBus.requestAnalyticsEventId,
      },
    });

    const getConfig = () => this.config;
    const getProfile = () => this.profile;
    const wrappedFetch = wrapFetch(clientFetch, getConfig, getProfile);
    this._api = createApi(wrappedFetch, getProfile);
    const getApi = () => this._api;
    const getObservability = () => this._observability;

    this._conversations = new V1ConversationsController({
      getConfig,
      getProfile,
      getApi,
      getObservability,
      requestAction: (action) => this.requestAction(action),
      getCurrentConversation: () => this._conversationViewModel?.conversation,
      withHistory,
    });

    this._conversationViewModel = new ConversationViewModel({
      id: NEW_CONVERSATION_ID,
      conversations: this.conversations,
    });
    this._conversationViewModel.stateObservable().subscribe((state) => {
      if (!state.loading) {
        this.finishConversationLoadMeasurement();
      }
    });

    this._historyViewModel = new HistoryViewModel({
      conversations: this.conversations,
      getCurrentConversationId: () => this.conversationViewModel.state.id,
      getCurrentOverview: () => this.conversationViewModel.state.overview,
      selectConversation: (id) => this.selectConversation(id),
    });

    this.stateStore.setValue((state) => {
      state.initialized = true;
    });

    this.handleParentMessage = this.handleParentMessage.bind(this);
  }

  init() {
    if (this._isActive) {
      return;
    }
    this._isActive = true;
    if (
      this.state.visibility === Visibility.Open &&
      this.conversationViewModel.state.conversation == null
    ) {
      this.startConversationLoadMeasurement();
    }
    this.subscribeToParentMessageBus();

    if (this.state.withHistory) {
      window.addEventListener('focus', this.handleWindowFocus);
    } else {
      window.addEventListener('storage', this.handleConversationStorage);
    }

    devLog('Threads app initialized with config:', this.config);
    sendWidgetInitialized(this.parentMessageBus);

    if (!this._didInitialLoad) {
      this._didInitialLoad = true;
      this.loadConversationsAndResumeIfNecessary();
    }
  }

  destroy() {
    if (!this._isActive) {
      return;
    }
    this._isActive = false;
    this.clearConversationLoadMeasurement();
    this.unsubscribeFromParentMessageBus();

    if (this.state.withHistory) {
      window.removeEventListener('focus', this.handleWindowFocus);
    } else {
      window.removeEventListener('storage', this.handleConversationStorage);
    }
  }

  get state(): AssistantState {
    return this.stateStore.getState();
  }

  get stateStore(): AssistantStateStore {
    return this._store;
  }

  get config(): AppConfig {
    return this.state.config;
  }

  get profile(): AppProfile {
    return this.state.profile;
  }

  get analytics(): Analytics {
    return this.observability.analytics ?? {};
  }

  get observability(): Observability {
    return this._observability;
  }

  private startConversationLoadMeasurement() {
    if (this._conversationLoadStartTime == null) {
      this._conversationLoadStartTime = performance.now();
    }
    return this._conversationLoadStartTime;
  }

  private clearConversationLoadMeasurement(
    expectedStartTime = this._conversationLoadStartTime,
  ) {
    if (this._conversationLoadStartTime === expectedStartTime) {
      this._conversationLoadStartTime = undefined;
    }
  }

  private finishConversationLoadMeasurement() {
    const conversationLoadStartTime = this._conversationLoadStartTime;
    if (conversationLoadStartTime == null) {
      return;
    }

    const {conversation, error, loading, loadResult} =
      this.conversationViewModel.state;
    if (loading) {
      return;
    }
    if (loadResult !== 'success' || conversation == null || error != null) {
      this.clearConversationLoadMeasurement(conversationLoadStartTime);
      return;
    }

    const duration = Math.round(performance.now() - conversationLoadStartTime);
    this.clearConversationLoadMeasurement(conversationLoadStartTime);
    this.observability.metrics?.timing(
      METRIC.conversation_load_duration,
      duration,
      {
        audience: this.config.audience,
        entry_point: this.config.entryPoint,
        implementation: 'threads',
      },
    );
  }

  get api(): Api {
    return this._api;
  }

  get parentMessageBus(): ParentMessageBus {
    return this._parentMessageBus;
  }

  get conversations(): LegacyConversationsController {
    return this._conversations;
  }

  get conversationViewModel(): ConversationViewModel {
    return this._conversationViewModel;
  }

  get historyViewModel(): HistoryViewModel {
    return this._historyViewModel;
  }

  selectConversation = (id: string): void => {
    const historyOverview = this._historyViewModel.state.overviews.find(
      (o) => o.id === id,
    );
    if (historyOverview) {
      this._conversationViewModel.seedOverview(historyOverview);
    }

    this._conversationViewModel.load(id);
  };

  startNewConversation = (): void => {
    this.startConversationLoadMeasurement();
    this.createNewConversation({
      triggerParams: {triggerPoint: 'assistant_new_conversation'},
    });
  };

  private handleWindowFocus = () => {
    if (this.state.visibility === Visibility.Closed) {
      return;
    }
    if (this.historyViewModel.state.isOpen) {
      this.historyViewModel.load();
    }
    const {conversation} = this.conversationViewModel;
    if (conversation && canFetchConversation(conversation)) {
      conversation.fetchConversation().catch(() => undefined);
    }
  };

  private handleConversationStorage = async (event: StorageEvent) => {
    const conversationId = this.conversationViewModel.state.id;
    if (
      !event.newValue ||
      !event.key?.startsWith(CONVERSATION_STORAGE_PREFIX) ||
      event.key === Conversation.storageKey(conversationId) ||
      this.state.visibility === Visibility.Closed
    ) {
      return;
    }

    const active = await this.loadLatestActiveConversation();
    if (
      active &&
      active.id !== conversationId &&
      this.conversationViewModel.state.id === conversationId &&
      !this.conversationViewModel.state.loading
    ) {
      this.selectConversation(active.id);
    }
  };

  private createNewConversation({
    initialQuery,
    triggerParams = this.state.triggerParams,
  }: {initialQuery?: string; triggerParams?: TriggerParams} = {}) {
    this.conversationViewModel.createNewConversation({
      context: triggerParams ? {triggerParams} : undefined,
      ...(initialQuery ? {initialMessage: initialQuery} : {}),
    });
  }

  requestRestart(params: {initialQuery?: string} = {}) {
    const {conversation} = this.conversationViewModel;
    if (isSupportChannel(conversation?.context.channel)) {
      this.stateStore.setValue((state) => {
        state.pendingConfirmation = {
          type: 'restart',
          ...(params.initialQuery ? {initialQuery: params.initialQuery} : {}),
        };
      });
      return;
    }

    if (conversation) {
      this.confirmRestart();
    } else {
      this.startConversationLoadMeasurement();
      this.createNewConversation({initialQuery: params.initialQuery});
    }
  }

  confirmRestart() {
    const {pendingConfirmation} = this.state;
    const initialQuery =
      pendingConfirmation?.type === 'restart'
        ? pendingConfirmation.initialQuery
        : undefined;

    this.stateStore.setValue((state) => {
      state.pendingConfirmation = null;
    });

    const {conversation} = this.conversationViewModel;
    if (conversation && !this.state.withHistory) {
      conversation.leave();
    }

    this.startConversationLoadMeasurement();
    this.createNewConversation({initialQuery});
  }

  cancelRestart() {
    this.stateStore.setValue((state) => {
      state.pendingConfirmation = null;
      if (state.triggerParams) {
        state.triggerParams = {triggerPoint: state.triggerParams.triggerPoint};
      }
    });
  }

  confirmClose() {
    return this.close(true);
  }

  cancelClose() {
    this.stateStore.setValue((state) => {
      state.pendingConfirmation = null;
    });
  }

  updateConfig(config: AppConfig) {
    this.stateStore.setValue((state) => {
      state.config = config;
      state.pendingConfirmation = null;
      state.viewMode = config.viewMode ?? DEFAULT_VIEW_MODE;
    });
  }

  updateProfile(profile: AppProfile) {
    this.stateStore.setValue((state) => {
      state.profile = profile;
    });
  }

  updateOnClose(onClose?: AppCloseHandler) {
    this.stateStore.setValue((state) => {
      state.onCloseHandler = onClose;
    });
  }

  updateParentMessageBus(parentMessageBus?: ParentMessageBus) {
    const nextParentMessageBus =
      parentMessageBus ??
      createParentMessageBus({
        audience: this.config.audience,
        entryPoint: this.config.entryPoint,
        parentOrigin: this.config.parentOrigin ?? window.origin,
      });

    if (this._parentMessageBus === nextParentMessageBus) {
      return;
    }

    this.unsubscribeFromParentMessageBus();
    this._parentMessageBus = nextParentMessageBus;

    if (this._isActive) {
      this.subscribeToParentMessageBus();
    }
  }

  private subscribeToParentMessageBus() {
    this.parentMessageBus.addListener(this.handleParentMessage);
  }

  private unsubscribeFromParentMessageBus() {
    this.parentMessageBus.removeListener(this.handleParentMessage);
  }

  requestAction(action: Omit<RequestAction, 'name'>): Promise<ClientResponse> {
    const nonce = uuid();
    const {waitForResponse, responseTimeout, action: requestedAction} = action;
    const parentMessagesObs = this._parentMessageSubject.asObservable();

    let response: Promise<ClientResponse>;

    if (waitForResponse) {
      const timeoutResponse = {
        code: 400,
        error: `Timeout waiting for request-action response for ${action.action.name}`,
      };

      response = firstValueFrom(
        parentMessagesObs.pipe(
          filter((m) => m.nonce === nonce),
          map((m) => {
            return {code: 200, data: m.data};
          }),
          timeout({
            each: responseTimeout ?? REQUEST_ACTION_DEFAULT_TIMEOUT_MS,
            with: () => {
              return of(timeoutResponse);
            },
          }),
        ),
      );
    } else {
      response = Promise.resolve({code: 200});
    }

    this.parentMessageBus.post({
      type: MessageToParentType.RequestAction,
      data: requestedAction,
      nonce,
    });

    return response;
  }

  open() {
    if (this.conversationViewModel.isNewConversation) {
      if (!this.conversationViewModel.state.loading) {
        this.startConversationLoadMeasurement();
      }
      this.createNewConversation();
    } else if (this.state.visibility === Visibility.Closed) {
      if (!this.conversationViewModel.state.loading) {
        this.startConversationLoadMeasurement();
      }
      this.conversationViewModel.load();
    }

    this.stateStore.setValue((state) => {
      state.visibility = Visibility.Open;
    });

    requestVisibilityChange(this.parentMessageBus, Visibility.Open);
  }

  async close(skipOnClose = false) {
    const {conversation} = this.conversationViewModel.state;
    const {onCloseHandler} = this.state;

    if (onCloseHandler && !skipOnClose) {
      const handled = onCloseHandler({
        close: (value) => this.close(value),
        conversation,
      });
      if (handled) {
        return;
      }
    }

    if (conversation) {
      const {channel, intent} = conversation.context;

      if (
        conversation.isActive &&
        intent === 'contact_support' &&
        channel === 'request_a_call'
      ) {
        this.minimize();
        return;
      }

      if (
        conversation.isActive &&
        !this.conversationViewModel.isEndedConversation &&
        intent === 'contact_support' &&
        channel === 'chat' &&
        !skipOnClose &&
        this.state.pendingConfirmation == null
      ) {
        this.stateStore.setValue((state) => {
          state.pendingConfirmation = {type: 'close'};
        });
        return;
      }

      // For live chat: end the conversation via the framework's leave(),
      // then either stay open (withHistory) or fall through below to close
      // the widget like any other channel.
      if (
        channel === 'chat' &&
        conversation.isActive &&
        !this.conversationViewModel.isEndedConversation
      ) {
        const result = await conversation.leave();
        this.stateStore.setValue((state) => {
          state.pendingConfirmation = null;
        });
        if (result.error) {
          // Automatic retries were exhausted, or the backend returned a
          // terminal error, so show the full error screen.
          this.conversationViewModel.setError({message: result.error});
          return;
        }

        // Optimistically update the overview to reflect ended state.
        // This makes isConversationOverviewEnded() return true immediately,
        // which hides the input and shows the "New chat" button.
        const currentOverview = this.conversationViewModel.state.overview;
        const liveChatPatch = {
          status: 'ended' as const,
          external_id: currentOverview?.live_chat?.external_id ?? '',
        };
        this.conversationViewModel.patchOverview({
          is_active: false,
          live_chat: liveChatPatch,
        });
        this.historyViewModel.patchOverview(conversation.id, {
          is_active: false,
          live_chat: liveChatPatch,
        });

        if (this.state.withHistory) {
          return;
        }
        // else: fall through to the shared close-widget logic below.
      } else if (
        !this.state.withHistory &&
        (channel !== 'bot' || this.config.closeEndsBotConversation)
      ) {
        conversation.leave();
      }
    }

    this.historyViewModel.close();
    this.clearConversationLoadMeasurement();

    this.stateStore.setValue((state) => {
      state.visibility = Visibility.Closed;
      state.pendingConfirmation = null;
    });

    if (!this.state.withHistory) {
      this.selectConversation(NEW_CONVERSATION_ID);
    }

    requestVisibilityChange(this.parentMessageBus, Visibility.Closed);
  }

  minimize() {
    this.historyViewModel.close();
    this.clearConversationLoadMeasurement();

    this.stateStore.setValue((state) => {
      state.visibility = Visibility.Minimized;
    });

    requestVisibilityChange(this.parentMessageBus, Visibility.Minimized);
  }

  private handleParentMessage(messageFromParent: MessageFromParent) {
    if (messageFromParent.type === MessageFromParentType.ViewportChange) {
      this.stateStore.setValue((state) => {
        state.parentViewportSnapshot = messageFromParent.data.viewportSnapshot;
      });
    }

    if (
      messageFromParent.type === MessageFromParentType.VisibilityChange &&
      ['open', 'closed', 'minimized'].includes(
        messageFromParent.data?.visibility,
      )
    ) {
      const incomingTriggerParams = messageFromParent.data?.triggerParams;
      const prevVisibility = this.state.visibility;
      const {conversation} = this.conversationViewModel.state;
      const isOpening = messageFromParent.data.visibility === Visibility.Open;
      const openBehavior = getTriggerPointOpenBehavior(
        incomingTriggerParams?.triggerPoint,
      );
      const isOpeningWithRefcode =
        isOpening && incomingTriggerParams?.refcode != null;
      const hasActiveConversation =
        conversation != null && conversation.isActive;
      const isLiveSupportChannel =
        conversation?.context.channel === 'chat' ||
        conversation?.context.channel === 'request_a_call';
      const isDifferentTriggerPoint =
        incomingTriggerParams?.triggerPoint !==
        conversation?.context.triggerParams?.triggerPoint;
      const shouldShowRestartConfirmation =
        isOpeningWithRefcode &&
        hasActiveConversation &&
        !isLiveSupportChannel &&
        isDifferentTriggerPoint;

      this.stateStore.setValue((state) => {
        state.visibility = messageFromParent.data.visibility;
        if (incomingTriggerParams) {
          state.triggerParams = incomingTriggerParams;
        }
        if (shouldShowRestartConfirmation) {
          state.pendingConfirmation = {
            type: 'restart',
            ...(incomingTriggerParams?.initialQuery
              ? {initialQuery: incomingTriggerParams.initialQuery}
              : {}),
          };
        }
      });

      if (!isOpening) {
        this.historyViewModel.close();
        this.clearConversationLoadMeasurement();
      }

      if (isOpening && !shouldShowRestartConfirmation) {
        if (prevVisibility === Visibility.Closed) {
          this.startConversationLoadMeasurement();
          this.openFromClosed(openBehavior, incomingTriggerParams);
        } else if (
          openBehavior === 'inject_or_new' &&
          incomingTriggerParams?.initialQuery
        ) {
          // The assistant is already open (or restoring from minimized):
          // send the smart suggestion into the current thread rather than
          // replacing it.
          this.injectTriggerParams(incomingTriggerParams);
        } else if (
          !this.state.withHistory &&
          incomingTriggerParams != null &&
          isCheckoutStudioTriggerPoint(incomingTriggerParams.triggerPoint) &&
          conversation != null &&
          conversation.isActive
        ) {
          // Without history there's nowhere to browse back to the current
          // thread, so reopening Checkout Studio restarts the bot
          // conversation instead of continuing whatever thread happens to
          // be in view, matching the legacy assistant's behavior. Live
          // support conversations are preserved.
          this.restartForCheckoutStudio(conversation, incomingTriggerParams);
        } else if (
          prevVisibility === Visibility.Minimized &&
          this.conversationViewModel.isNewConversation
        ) {
          this.startConversationLoadMeasurement();
          this.createOrResumeConversation();
        }
      }
    }

    if (messageFromParent.type === MessageFromParentType.CheckInitialized) {
      if (this._isActive) {
        sendWidgetInitialized(this.parentMessageBus);
      }
    }

    this._parentMessageSubject.next(messageFromParent);
  }

  // Handle the assistant opening from a fully closed state based on the
  // trigger point that opened it.
  private openFromClosed(
    behavior: TriggerPointOpenBehavior,
    triggerParams?: TriggerParams,
  ) {
    const {initialQuery, initialAttachment} = triggerParams ?? {};

    // Contextual and smart-suggestion entry points always start a fresh
    // thread when the assistant was closed, as do trigger points carrying
    // initial content, so that content is never delivered into an existing
    // thread the user wasn't looking at.
    if (behavior !== 'resume_latest' || initialQuery || initialAttachment) {
      this.createNewConversation({initialQuery});
      return;
    }

    if (
      this.conversationViewModel.isNewConversation ||
      this.conversationViewModel.isEndedConversation
    ) {
      this.createOrResumeConversation();
      return;
    }

    this.conversationViewModel.load();
  }

  // Send a smart-suggestion query into the current thread. Live support
  // conversations can't accept injected queries (the client would silently
  // drop the action), so start a new thread carrying the query instead.
  private async injectTriggerParams(triggerParams: TriggerParams) {
    const {conversation} = this.conversationViewModel.state;
    const isLiveSupportChannel =
      conversation?.context.channel === 'chat' ||
      conversation?.context.channel === 'request_a_call';

    if (!conversation || !conversation.isActive || isLiveSupportChannel) {
      this.createNewConversation({initialQuery: triggerParams.initialQuery});
      return;
    }

    // Without history there's nowhere to browse back to the current thread,
    // so start-fresh trigger points (e.g. payments_analytics) restart
    // instead of injecting into it, matching the no-history behavior.
    if (
      !this.state.withHistory &&
      isStartFreshTriggerPoint(triggerParams.triggerPoint)
    ) {
      conversation.leave();
      this.createNewConversation({initialQuery: triggerParams.initialQuery});
      return;
    }

    await conversation.action({
      name: 'handle-trigger-params',
      triggerParams,
    });
  }

  // Restart the bot conversation for a Checkout Studio trigger point fired
  // while the assistant is already open. Live support conversations are
  // preserved rather than restarted.
  private restartForCheckoutStudio(
    conversation: Conversation,
    triggerParams: TriggerParams,
  ) {
    const isLiveSupportChannel =
      conversation.context.channel === 'chat' ||
      conversation.context.channel === 'request_a_call';

    if (isLiveSupportChannel) {
      return;
    }

    conversation.leave();
    this.createNewConversation({initialQuery: triggerParams.initialQuery});
  }

  // Resume the latest active conversation if one exists; otherwise start a
  // new one. Only active conversations are ever reopened this way — ended
  // conversations stay in history.
  private async createOrResumeConversation() {
    const {triggerParams} = this.state;
    const {initialQuery, initialAttachment} = triggerParams ?? {};

    // Initial content always starts a new conversation rather than being
    // appended to whichever thread happens to be resumable.
    if (initialQuery || initialAttachment) {
      await this.createNewConversation({initialQuery});
      return;
    }

    // `resumeOrCreate` marks the conversation as loading before the history
    // lookup starts, so the screen shows the loading state (rather than the
    // new-conversation input) while resolving which conversation to show.
    await this.conversationViewModel.resumeOrCreate(
      () => this.loadLatestActiveConversation(),
      {context: triggerParams ? {triggerParams} : undefined},
    );
  }

  private async loadConversationsAndResumeIfNecessary() {
    const conversationLoadStartTime = this._conversationLoadStartTime;
    const active = await this.loadLatestActiveConversation();
    if (!active) {
      if (!this.conversationViewModel.state.loading) {
        this.clearConversationLoadMeasurement(conversationLoadStartTime);
      }
      return;
    }

    const shouldOpen =
      this._isActive &&
      this.state.visibility !== Visibility.Open &&
      (this.config.resumeVisibilityState === Visibility.Open ||
        active.live_chat?.status === 'active' ||
        active.request_a_call?.status === 'queued');
    if (shouldOpen) {
      this.startConversationLoadMeasurement();
    }
    this.selectConversation(active.id);

    if (!shouldOpen) {
      return;
    }

    if (this.config.resumeVisibilityState === Visibility.Open) {
      this.open();
      return;
    }

    if (
      active.live_chat?.status === 'active' ||
      active.request_a_call?.status === 'queued'
    ) {
      this.open();
    }
  }

  private async loadLatestActiveConversation() {
    const result = await this.historyViewModel.load({limit: 10});
    if (result.type === 'error') {
      return;
    }

    const activeChat = result.data.find(
      (o) => o.live_chat?.status === 'active',
    );
    if (activeChat) {
      return activeChat;
    }

    const queuedRequestACall = result.data.find(
      (o) => o.request_a_call?.status === 'queued',
    );
    if (queuedRequestACall) {
      return queuedRequestACall;
    }

    return result.data.find((o) => o.is_active);
  }
}
